'use client';

import { useState, useEffect, useRef } from 'react';
import styles from './HeroBulletTicker.module.css';

interface HeroBulletTickerProps {
  lang?: 'en' | 'de';
  containerClassName?: string;
}

const bullets = [
  'Stop Hibernating Tools -- Turn your AI stack into a system that ships weekly',
  'Don\'t Get Terminated -- Be visible + cite-worthy for machines and humans',
  'No More "We Tried AI" -- Build workflows your team still uses in week two',
  'Ship Faster. Stay Human. -- AI speed + human nuance, no brand-voice collapse',
  'From Chaos to Growth Blueprint -- KPIs, roadmap, and next moves',
  'Performance Without Guesswork -- Tracking, targeting, creative, iteration',
  'Human-in-the-Loop, Always -- Automation runs, experts approve what matters',
  'Make Your Website Sell While You Sleep -- Structure + speed + clarity that converts',
  'GEO/AEO Ready by Design -- Built for answer engines, not just clicks',
  'Your Marketing System > Your Effort -- Replace randomness with repeatable growth',
  'Outsider Perspective, Insider Results -- Find opportunities you can\'t see internally',
  'Workshops That Actually Stick -- We build it and teach your team to run it'
];

// Split bullet text by " -- " delimiter
function splitBulletText(text: string): { first: string; second: string | null } {
  const parts = text.split(' -- ');
  return {
    first: parts[0] || text,
    second: parts[1] || null
  };
}

export default function HeroBulletTicker({ lang = 'en', containerClassName = '' }: HeroBulletTickerProps) {
  const [isSlowed, setIsSlowed] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const [lastPointerX, setLastPointerX] = useState(0);
  const [lastPointerTime, setLastPointerTime] = useState(0);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const autoplayTimeRef = useRef<number>(0);
  const pointerId = useRef<number | null>(null);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Autoplay animation loop
  useEffect(() => {
    if (prefersReducedMotion || isDragging) return;

    let lastTime = performance.now();
    const baseSpeed = isSlowed ? 30 : 60; // pixels per second (news ticker speed)

    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - lastTime) / 1000; // Convert to seconds
      lastTime = currentTime;

      autoplayTimeRef.current += deltaTime * baseSpeed;

      // Reset when we've scrolled through one full set (50% of total width)
      if (trackRef.current) {
        const trackWidth = trackRef.current.scrollWidth / 2; // Divide by 2 because we have 2 copies
        if (autoplayTimeRef.current >= trackWidth) {
          autoplayTimeRef.current = 0;
        }
        
        // CRITICAL FIX: Directly update DOM transform for smooth animation
        trackRef.current.style.transform = `translate3d(${-autoplayTimeRef.current}px, 0, 0)`;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [prefersReducedMotion, isDragging, isSlowed]);

  // Handle pointer down (start drag)
  const handlePointerDown = (e: React.PointerEvent) => {
    if (prefersReducedMotion) return;

    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    pointerId.current = e.pointerId;
    
    setIsDragging(true);
    setLastPointerX(e.clientX);
    setLastPointerTime(performance.now());
    setVelocity(0);
    
    // Pause autoplay
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // Handle pointer move (dragging)
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || pointerId.current !== e.pointerId) return;
    
    const currentX = e.clientX;
    const currentTime = performance.now();
    const deltaX = currentX - lastPointerX;
    const deltaTime = currentTime - lastPointerTime;
    
    // Calculate velocity for momentum (pixels per millisecond)
    if (deltaTime > 0) {
      setVelocity(deltaX / deltaTime);
    }
    
    // Update drag offset
    setDragOffset(prev => prev + deltaX);
    setLastPointerX(currentX);
    setLastPointerTime(currentTime);
  };

  // Handle pointer up (end drag)
  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDragging || pointerId.current !== e.pointerId) return;
    
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    pointerId.current = null;
    
    setIsDragging(false);
    
    // Apply momentum/inertia
    if (Math.abs(velocity) > 0.1) {
      applyMomentum(velocity);
    } else {
      // Resume autoplay immediately
      resumeAutoplay();
    }
  };

  // Apply momentum decay after drag release
  const applyMomentum = (initialVelocity: number) => {
    let currentVelocity = initialVelocity * 1000; // Convert to pixels per second
    const friction = 0.92; // Friction coefficient
    let lastTime = performance.now();

    const decay = (currentTime: number) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      currentVelocity *= friction;
      const deltaOffset = currentVelocity * deltaTime;
      
      const newOffset = dragOffset + deltaOffset;
      setDragOffset(newOffset);
      
      // CRITICAL FIX: Apply transform immediately during momentum
      if (trackRef.current) {
        trackRef.current.style.transform = `translate3d(${-autoplayTimeRef.current + newOffset}px, 0, 0)`;
      }
      
      // Continue until velocity is negligible
      if (Math.abs(currentVelocity) > 10) {
        animationFrameRef.current = requestAnimationFrame(decay);
      } else {
        // Momentum ended, resume autoplay
        resumeAutoplay();
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(decay);
  };

  // Resume autoplay with smooth transition (300ms blend)
  const resumeAutoplay = () => {
    const startTime = performance.now();
    const startOffset = dragOffset;
    const blendDuration = 300; // milliseconds
    const baseSpeed = isSlowed ? 30 : 60; // pixels per second

    // Normalize offset to prevent jumps
    if (trackRef.current) {
      const trackWidth = trackRef.current.scrollWidth / 2;
      const normalizedOffset = ((startOffset % trackWidth) + trackWidth) % trackWidth;
      autoplayTimeRef.current = normalizedOffset;
      setDragOffset(0);
    }

    const blend = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / blendDuration, 1);
      
      // Ease out cubic for smooth transition
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(blend);
      } else {
        // Blend complete, resume normal autoplay
        setIsDragging(false);
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(blend);
  };

  if (prefersReducedMotion) {
    // Static grid layout for reduced motion - SSR-safe semantic structure
    return (
      <section aria-label="Key outcomes" className={`${styles.staticContainer} ${containerClassName}`}>
        <ul className={styles.staticGrid}>
          {bullets.map((bullet, index) => {
            const { first, second } = splitBulletText(bullet);
            return (
              <li key={index} className={styles.staticCard}>
                <span className={styles.glintDot} aria-hidden="true">✦</span>
                <span className={styles.bulletText}>
                  <span className={styles.bulletFirst}>{first}</span>
                  {second && (
                    <>
                      <span className={styles.bulletSeparator}> — </span>
                      <span className={styles.bulletSecond}>{second}</span>
                    </>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  return (
    <section aria-label="Key outcomes" className={`${styles.tickerWrapper} ${containerClassName}`}>
      <div className={styles.frameContainer}>
        <div className={styles.fadeEdgeLeft} aria-hidden="true" />
        <div 
          ref={containerRef}
          className={`${styles.tickerContainer} ${isDragging ? styles.dragging : ''}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onMouseEnter={() => !isDragging && setIsSlowed(true)}
          onMouseLeave={() => setIsSlowed(false)}
          role="marquee"
          aria-live="off"
        >
          <div 
            ref={trackRef}
            className={styles.tickerTrack}
          >
            {/* Render two identical tracks for seamless infinite loop */}
            {[...bullets, ...bullets].map((bullet, index) => {
              const { first, second } = splitBulletText(bullet);
              return (
                <div key={index} className={styles.tickerItem}>
                  <span className={styles.glintDot} aria-hidden="true">✦</span>
                  <span className={styles.bulletText}>
                    <span className={styles.bulletFirst}>{first}</span>
                    {second && (
                      <>
                        <span className={styles.bulletSeparator}> — </span>
                        <span className={styles.bulletSecond}>{second}</span>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles.fadeEdgeRight} aria-hidden="true" />
      </div>
      
      {/* SSR-safe semantic structure for crawlers */}
      <ul className={styles.srOnly}>
        {bullets.map((bullet, index) => {
          const { first, second } = splitBulletText(bullet);
          return (
            <li key={index}>
              <span>{first}</span>
              {second && <span> — {second}</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}